import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { extractErrorMessage } from '../../api/errors';
import { getTenantLicense, type License } from '../../api/licenses';
import { formatDate } from '../../util/datetime';
import { LicenseSummary } from './LicenseSummary';

/** What the license means right now, in one sentence. */
export function stateLine(l: License): { severity: 'info' | 'warning' | 'error'; text: string } | null {
  if (l.revoked_at) {
    return {
      severity: 'error',
      text:
        'This organisation’s license was revoked, so it is read-only: everyone can still sign ' +
        'in, view and export, but new recordings, enrollments and devices are blocked until ' +
        'a new license is issued.',
    };
  }
  if (l.state === 'read_only') {
    return {
      severity: 'error',
      text:
        'This organisation is read-only: everyone can still sign in, view and export, but ' +
        'new recordings, enrollments and devices are blocked until the license is renewed.',
    };
  }
  if (l.state === 'grace' && l.grace_ends_at) {
    return {
      severity: 'warning',
      text: `The license has ended. Everything keeps working until ${formatDate(l.grace_ends_at)}; then the organisation becomes read-only.`,
    };
  }
  if (l.days_left !== null && l.days_left <= 45) {
    return {
      severity: 'warning',
      text: `The license ends in ${l.days_left} day${l.days_left === 1 ? '' : 's'}. Contact your 10NEWTONS representative to renew.`,
    };
  }
  if (l.procedure_level === 'blocked') {
    return {
      severity: 'error',
      text:
        'The license’s procedures are used up: no new procedures can be started until the ' +
        'limit is raised. Everything already captured is kept.',
    };
  }
  if (l.seat_level === 'blocked' || l.device_level === 'blocked') {
    return {
      severity: 'error',
      text: 'The license limit has been passed: new participants or devices are refused.',
    };
  }
  if (l.seat_level === 'over' || l.device_level === 'over' || l.procedure_level === 'over') {
    return {
      severity: 'warning',
      text: 'You are over a license limit. A little more is allowed; beyond that, new ones are refused.',
    };
  }
  return null;
}

/** Settings → License (TENANT-LICENSES): key, dates, state and usage. */
export function TenantLicensePanel({ tenantId }: { tenantId: string }) {
  const [license, setLicense] = useState<License | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getTenantLicense(tenantId).then(setLicense, (err) =>
      setError(extractErrorMessage(err, 'could not load the license')),
    );
  }, [tenantId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (license === undefined) return <CircularProgress aria-label="Loading" />;
  if (license === null) {
    return (
      <Typography color="text.secondary">
        No license has been issued to this organisation yet. Your 10NEWTONS representative
        issues it; until then nothing is limited.
      </Typography>
    );
  }
  const line = stateLine(license);
  return (
    <Stack spacing={2}>
      {line && <Alert severity={line.severity}>{line.text}</Alert>}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <LicenseSummary license={license} />
      </Paper>
      {license.subs.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Organisations under this license
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            They share its dates and its participant and device limits; each has its own key.
          </Typography>
          {license.subs.map((s) => (
            <Typography key={s.id} variant="body2">
              {s.tenant_name} — <code>{s.key}</code>
            </Typography>
          ))}
        </Paper>
      )}
      <Typography variant="caption" color="text.secondary">
        Participants count while their access is on; devices count until they are retired;
        procedures count from the start of the current license term.
        Admins are emailed 45, 30, 14, 7 and 1 days before the end date.
      </Typography>
    </Stack>
  );
}
